import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faSlidersH, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { useCurrency } from '../context/CurrencyContext';
import FilterSortPanel from '../JSComponents/FilterSortPanel';
import ProductCard from '../JSComponents/ProductCard';
import Pagination from '../JSComponents/Pagination';
import usePaginationGuard from '../hooks/usePaginationGuard';
import '../StylesComponents/featured-page.css';

const categories = ['T-Shirts', 'Hoodies', 'Sweaters', 'Activewear', 'Jeans', 'Pants', 'Shorts', 'Jackets', 'Swimwear', 'Shoes', 'Accessories'];
const sortOptions = [
  { value: 'Default', label: 'DEFAULT' },
  { value: 'PriceLowToHigh', label: 'PRICE: LOW_TO_HIGH' },
  { value: 'PriceHighToLow', label: 'PRICE: HIGH_TO_LOW' },
  { value: 'DiscountHighToLow', label: 'BIGGEST_DISCOUNT' },
  { value: 'Newest', label: 'NEWEST_FIRST' },
];

function KidsPage({ currentUser }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { exchangeRates, currency } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const itemsPerPage = 48;
  const [allBrands, setAllBrands] = useState([]);
  const [allRetailers, setAllRetailers] = useState([]);
  const [isFilterSortOpen, setIsFilterSortOpen] = useState(false);
  const [panelView, setPanelView] = useState('filter');

  const [currentPage, setCurrentPage] = useState(() => {
    return parseInt(searchParams.get('page')) || 1;
  });

  const [selectedSort, setSelectedSort] = useState(() => {
    return searchParams.get('sort') || 'Default';
  });

  const [selectedMaxPrice, setSelectedMaxPrice] = useState(() => {
    return parseInt(searchParams.get('maxPrice')) || 1000;
  });

  const [selectedPriceRanges, setSelectedPriceRanges] = useState(() => {
    const param = searchParams.get('priceRanges');
    return param ? param.split(',') : [];
  });

  const [selectedDiscountRanges, setSelectedDiscountRanges] = useState(() => {
    const param = searchParams.get('discountRanges');
    return param ? param.split(',') : [];
  });

  const [selectedSizes, setSelectedSizes] = useState(() => {
    const param = searchParams.get('sizes');
    return param ? param.split(',') : [];
  });

  const [selectedColors, setSelectedColors] = useState(() => {
    const param = searchParams.get('colors');
    return param ? param.split(',') : [];
  });

  const [multiOfferOnly, setMultiOfferOnly] = useState(() => {
    return searchParams.get('multiOffer') === 'true';
  });

  const [selectedFilters, setSelectedFilters] = useState(() => {
    const catParam = searchParams.get('category');
    return catParam ? catParam.split(',') : [];
  });

  const [selectedBrands, setSelectedBrands] = useState(() => {
    const brandParam = searchParams.get('brand');
    return brandParam ? brandParam.split(',') : [];
  });

  const [selectedRetailers, setSelectedRetailers] = useState(() => {
    const retailerParam = searchParams.get('retailers');
    return retailerParam ? retailerParam.split(',') : [];
  });

  const [selectedGenders, setSelectedGenders] = useState(['kids']);

  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('search') || '');

  const isInitialMount = useRef(true);
  const lastFetchParams = useRef('');
  const discoveryAnchor = useRef(new Date().toISOString());

  usePaginationGuard({ products, totalProducts, currentPage, itemsPerPage, setCurrentPage });

  const filterConfig = {
    priceRanges: [
      { id: '0-50', label: '$0 - $50' },
      { id: '50-100', label: '$50 - $100' },
      { id: '100-200', label: '$100 - $200' },
      { id: '200+', label: '$200+' },
    ],
    discountRanges: [
      { id: '30+', label: '30% OFF OR MORE' },
      { id: '50+', label: '50% OFF OR MORE' },
      { id: '70+', label: '70% OFF OR MORE' },
    ],
    sizes: [
      { label: 'Infant', values: ['0-3M', '3-6M', '6-12M', '12-18M'] },
      { label: 'Toddler', values: ['2T', '3T', '4T', '5T'] },
      { label: 'Youth', values: ['XS', 'S', 'M', 'L', 'XL'] },
      { label: 'Footwear', values: ['4K', '5K', '6K', '7K', '8K', '9K', '10K'] }
    ],
    colors: [
      { id: 'black', label: 'Black', hex: '#000000' },
      { id: 'white', label: 'White', hex: '#FFFFFF' },
      { id: 'gray', label: 'Gray', hex: '#808080' },
      { id: 'navy', label: 'Navy', hex: '#000080' },
      { id: 'red', label: 'Red', hex: '#FF0000' },
    ]
  };

  const activeFilters = [
    ...selectedFilters.map(f => ({ id: f, label: f.toUpperCase(), onRemove: (id) => setSelectedFilters(prev => prev.filter(x => x !== id)) })),
    ...selectedBrands.map(f => ({ id: f, label: `BRAND: ${f.toUpperCase()}`, onRemove: (id) => setSelectedBrands(prev => prev.filter(x => x !== id)) })),
    ...selectedRetailers.map(f => ({ id: f, label: `RETAILER: ${f.toUpperCase()}`, onRemove: (id) => setSelectedRetailers(prev => prev.filter(x => x !== id)) })),
    ...selectedGenders.map(f => ({ id: f, label: `GENDER: ${f.toUpperCase()}`, onRemove: (id) => setSelectedGenders(prev => prev.filter(x => x !== id)) })),
    ...(multiOfferOnly ? [{ id: 'multiOffer', label: 'COMPARISON_DEALS_ONLY', onRemove: () => setMultiOfferOnly(false) }] : []),
    ...(searchTerm ? [{ id: 'search', label: `SEARCH: ${searchTerm.toUpperCase()}`, onRemove: () => setSearchTerm('') }] : []),
    ...(selectedMaxPrice < 1000 ? [{ id: 'maxPrice', label: `PRICE: UNDER $${selectedMaxPrice}`, onRemove: () => setSelectedMaxPrice(1000) }] : []),
    ...selectedPriceRanges.map(f => ({ id: f, label: f, onRemove: (id) => setSelectedPriceRanges(prev => prev.filter(x => x !== id)) })),
    ...selectedDiscountRanges.map(f => ({ id: f, label: `OFF: ${f}`, onRemove: (id) => setSelectedDiscountRanges(prev => prev.filter(x => x !== id)) })),
    ...selectedSizes.map(f => ({ id: f, label: `SIZE: ${f}`, onRemove: (id) => setSelectedSizes(prev => prev.filter(x => x !== id)) })),
    ...selectedColors.map(f => ({ id: f, label: `COLOR: ${f.toUpperCase()}`, onRemove: (id) => setSelectedColors(prev => prev.filter(x => x !== id)) })),
  ];

  const clearAllFilters = () => {
    setSelectedFilters([]);
    setSelectedBrands([]);
    setSelectedRetailers([]);
    setSelectedPriceRanges([]);
    setSelectedDiscountRanges([]);
    setSelectedSizes([]);
    setSelectedColors([]);
    setMultiOfferOnly(false);
    setSelectedGenders(['kids']);
    setSelectedMaxPrice(1000);
    setSearchTerm('');
    setCurrentPage(1);
  };

  // Consolidated URL Synchronization (State -> URL)
  useEffect(() => {
    if (isInitialMount.current) return;

    const params = new URLSearchParams();
    if (currentPage > 1) params.set('page', currentPage);
    if (selectedSort !== 'Default') params.set('sort', selectedSort);
    if (selectedMaxPrice < 1000) params.set('maxPrice', selectedMaxPrice);
    if (selectedFilters.length > 0) params.set('category', selectedFilters.join(','));
    if (selectedBrands.length > 0) params.set('brand', selectedBrands.join(','));
    if (selectedRetailers.length > 0) params.set('retailers', selectedRetailers.join(','));
    if (searchTerm) params.set('search', searchTerm);
    if (multiOfferOnly) params.set('multiOffer', 'true');
    if (selectedPriceRanges.length > 0) params.set('priceRanges', selectedPriceRanges.join(','));
    if (selectedDiscountRanges.length > 0) params.set('discountRanges', selectedDiscountRanges.join(','));
    if (selectedSizes.length > 0) params.set('sizes', selectedSizes.join(','));
    if (selectedColors.length > 0) params.set('colors', selectedColors.join(','));
    
    const currentParams = searchParams.toString();
    const newParams = params.toString();
    
    if (currentParams !== newParams) {
      setSearchParams(params, { replace: true });
    }
  }, [
    currentPage, selectedSort, selectedMaxPrice, selectedFilters, 
    selectedBrands, selectedRetailers, searchTerm, multiOfferOnly,
    selectedPriceRanges, selectedDiscountRanges, selectedSizes, selectedColors
  ]);

  // Sync state FROM URL (URL -> State)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const catParam = searchParams.get('category') || '';
    if (catParam !== selectedFilters.join(',')) setSelectedFilters(catParam ? catParam.split(',') : []);
    
    const brandParam = searchParams.get('brand') || '';
    if (brandParam !== selectedBrands.join(',')) setSelectedBrands(brandParam ? brandParam.split(',') : []);

    const retailerParam = searchParams.get('retailers') || '';
    if (retailerParam !== selectedRetailers.join(',')) setSelectedRetailers(retailerParam ? retailerParam.split(',') : []);

    const priceParam = parseInt(searchParams.get('maxPrice')) || 1000;
    if (priceParam !== selectedMaxPrice) setSelectedMaxPrice(priceParam);

    const searchParam = searchParams.get('search') || '';
    if (searchParam !== searchTerm) setSearchTerm(searchParam);

    const pageParam = parseInt(searchParams.get('page')) || 1;
    if (pageParam !== currentPage) setCurrentPage(pageParam);

    const sortParam = searchParams.get('sort') || 'Default';
    if (sortParam !== selectedSort) setSelectedSort(sortParam);

    const prParam = searchParams.get('priceRanges') || '';
    if (prParam !== selectedPriceRanges.join(',')) setSelectedPriceRanges(prParam ? prParam.split(',') : []);

    const drParam = searchParams.get('discountRanges') || '';
    if (drParam !== selectedDiscountRanges.join(',')) setSelectedDiscountRanges(drParam ? drParam.split(',') : []);

    const szParam = searchParams.get('sizes') || '';
    if (szParam !== selectedSizes.join(',')) setSelectedSizes(szParam ? szParam.split(',') : []);

    const coParam = searchParams.get('colors') || '';
    if (coParam !== selectedColors.join(',')) setSelectedColors(coParam ? coParam.split(',') : []);

    const moParam = searchParams.get('multiOffer') === 'true';
    if (moParam !== multiOfferOnly) setMultiOfferOnly(moParam);
  }, [searchParams]);

  // Combined Data Fetcher
  useEffect(() => {
    const fetchData = async () => {
      const rate = exchangeRates[currency] || 1;
      const maxPriceAUD = selectedMaxPrice / rate;
      const interests = localStorage.getItem('stockroom_interests');

      const fetchParamsFingerprint = JSON.stringify({
        selectedFilters, selectedBrands, selectedRetailers, selectedSort, selectedGenders, 
        maxPriceAUD, selectedPriceRanges, selectedDiscountRanges, 
        selectedSizes, selectedColors, currency, searchTerm, 
        multiOfferOnly, currentPage
      });

      if (lastFetchParams.current === fetchParamsFingerprint) return;
      lastFetchParams.current = fetchParamsFingerprint;

      try {
        setLoading(true);
        const [productsRes, brandsRes, retailersRes] = await Promise.all([
          api.get('/api/products', {
            params: {
              categories: selectedFilters.join(','),
              brand: selectedBrands.join(','),
              retailers: selectedRetailers.join(','),
              gender: selectedGenders.join(','),
              maxPrice: maxPriceAUD,
              priceRanges: selectedPriceRanges.join(','),
              discountRanges: selectedDiscountRanges.join(','),
              sizes: selectedSizes.join(','),
              colors: selectedColors.join(','),
              sort: selectedSort,
              multiOfferOnly: multiOfferOnly,
              interests: interests,
              search: searchTerm,
              page: currentPage,
              limit: itemsPerPage,
              anchorTime: discoveryAnchor.current
            },
          }),
          api.get('/api/products/brands', {
            params: {
              categories: selectedFilters.join(','),
              gender: selectedGenders.join(','),
              maxPrice: maxPriceAUD,
              search: searchTerm
            }
          }),
          api.get('/api/products/retailers', {
            params: {
              categories: selectedFilters.join(','),
              gender: selectedGenders.join(','),
              maxPrice: maxPriceAUD,
              search: searchTerm
            }
          })
        ]);
        
        setProducts(productsRes.data.products || []);
        setTotalProducts(productsRes.data.total || 0);
        setAllBrands(Array.isArray(brandsRes.data) ? brandsRes.data : []);
        setAllRetailers(Array.isArray(retailersRes.data) ? retailersRes.data : []);
      } catch (err) {
        console.error('FETCH_ERROR:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    if (currentPage > 1) window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [
    selectedFilters, selectedBrands, selectedRetailers, selectedSort, selectedGenders, 
    selectedMaxPrice, selectedPriceRanges, selectedDiscountRanges, 
    selectedSizes, selectedColors, currency, exchangeRates, 
    searchTerm, multiOfferOnly, currentPage
  ]);

  return (
    <div className="featured-page-master">
      <header className="page-header-technical">
        <div className="header-crumb">HOME // COLLECTIONS // KIDS</div>
        <h1 className="page-main-title">KIDS_MARKET_FEED</h1>
      </header>

      <div className="catalog-controls-technical">
        <div className="controls-left">
          <button className="tech-control-btn filter-btn" onClick={() => { setPanelView('filter'); setIsFilterSortOpen(true); }}>
            <div className="btn-content">
              <FontAwesomeIcon icon={faSlidersH} className="control-icon" />
              <div className="btn-text">
                <span className="label">REFINE</span>
                <span className="value">SEARCH</span>
              </div>
            </div>
            <div className="btn-status"></div>
          </button>
        </div>
        
        <div className="controls-center">
          <div className="inventory-hud">
            <div className="hud-status">
              <span className="pulse-dot"></span>
              SYSTEM_ACTIVE
            </div>
            <div className="hud-metrics">
              <div className="metric">
                <span className="metric-label">LOCATED_OBJECTS</span>
                <span className="metric-value">{totalProducts}</span>
              </div>
              <div className="metric-divider"></div>
              <div className="metric">
                <span className="metric-label">DB_STATUS</span>
                <span className="metric-value">SYNCED</span>
              </div>
            </div>
          </div>
        </div>

        <div className="controls-right">
          <button className="tech-control-btn sort-btn" onClick={() => { setPanelView('sort'); setIsFilterSortOpen(true); }}>
            <div className="btn-content">
              <div className="btn-text">
                <span className="label">SORT_BY</span>
                <span className="value">{selectedSort.replace(/([A-Z])/g, '_$1').toUpperCase().replace(/^_/, '')}</span>
              </div>
              <FontAwesomeIcon icon={faChevronRight} className="control-icon arrow" />
            </div>
            <div className="btn-status"></div>
          </button>
        </div>
      </div>

      <main className="product-catalog-grid">
        {loading && products.length === 0 ? (
          <div className="catalog-loading">
            <FontAwesomeIcon icon={faSpinner} spin />
            <span>SYNCING_INVENTORY...</span>
          </div>
        ) : (
          <>
            {products.map((product, index) => (
              <div key={`${product._id || product.id}-${index}`} className="catalog-item">
                <ProductCard product={product} />
              </div>
            ))}
            <Pagination
              currentPage={currentPage}
              totalPages={Math.ceil(totalProducts / itemsPerPage)}
              onPageChange={setCurrentPage}
              isLoading={loading}
            />
          </>
        )}
      </main>

      <FilterSortPanel
        isOpen={isFilterSortOpen}
        onClose={() => setIsFilterSortOpen(false)}
        isFilterView={panelView === 'filter'}
        visiblePrimaries={['Tops', 'Bottoms', 'Outerwear', 'Footwear', 'Accessories']}
        categories={categories}
        brands={allBrands}
        retailers={allRetailers}
        sortOptions={sortOptions}
        selectedFilters={selectedFilters}
        selectedBrands={selectedBrands}
        selectedRetailers={selectedRetailers}
        selectedGenders={selectedGenders}
        selectedSort={selectedSort}
        selectedMaxPrice={selectedMaxPrice}
        handleMaxPriceChange={setSelectedMaxPrice}
        handleFilterChange={(cat) => { setSelectedFilters(prev => prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]); setCurrentPage(1); }}
        handleBrandChange={(brand) => { setSelectedBrands(prev => prev.includes(brand) ? prev.filter(b => b !== brand) : [...prev, brand]); setCurrentPage(1); }}
        handleRetailerChange={(retailer) => { setSelectedRetailers(prev => prev.includes(retailer) ? prev.filter(r => r !== retailer) : [...prev, retailer]); setCurrentPage(1); }}
        handleGenderChange={(gender) => { setSelectedGenders(prev => prev.includes(gender) ? prev.filter(x => x !== gender) : [...prev, gender]); setCurrentPage(1); }}
        handleSortChange={(sort) => { setSelectedSort(sort); setIsFilterSortOpen(false); setCurrentPage(1); }}
        filterConfig={filterConfig}
        activeFilters={activeFilters}
        selectedPriceRanges={selectedPriceRanges}
        handlePriceRangeChange={(id) => { setSelectedPriceRanges(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]); setCurrentPage(1); }}
        selectedDiscountRanges={selectedDiscountRanges}
        handleDiscountRangeChange={(id) => { setSelectedDiscountRanges(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]); setCurrentPage(1); }}
        selectedSizes={selectedSizes}
        handleSizeChange={(size) => { setSelectedSizes(prev => prev.includes(size) ? prev.filter(x => x !== size) : [...prev, size]); setCurrentPage(1); }}
        selectedColors={selectedColors}
        handleColorChange={(color) => { setSelectedColors(prev => prev.includes(color) ? prev.filter(x => x !== color) : [...prev, color]); setCurrentPage(1); }}
        multiOfferOnly={multiOfferOnly}
        handleMultiOfferChange={(val) => { setMultiOfferOnly(val); setCurrentPage(1); }}
        clearAllFilters={clearAllFilters}
      />
    </div>
  );
}

export default KidsPage;
